import { type FlexMenu, type FlexProduct } from "~/api/menus/types";
import { calculateUnitGrossPrice } from "~/utils/calculate-unit-gross-price";

import { type PreOrderLine } from "./orders-create-context";
import { discountedUnitPriceInclVat } from "./pre-order-unit-price";
import { sumVariantDeposits, type VariantDepositSum } from "./variant-deposits";

export type PricedPreOrderLine = {
  /** Product and picked extras under the hybrid-billing discount, whole minor units. */
  unitPriceInclVat: number;
  /** The product's deposit plus each picked choice's, per unit. */
  depositInclVat: number;
  variantDeposits: VariantDepositSum;
};

function extraUnitPriceInclVat(
  variants: FlexMenu["variants"],
  quantityByChoiceId: Record<string, number>,
  vatRate: number
): number {
  return variants
    .flatMap((variant) => variant.choices)
    .reduce((total, choice) => {
      const quantity = quantityByChoiceId[choice.id] ?? 0;
      if (quantity <= 0) return total;

      return (
        total +
        quantity *
          calculateUnitGrossPrice(choice.unitPriceExclVat, vatRate, null)
      );
    }, 0);
}

/**
 * The one place a pre-order line is priced. The menu row, the variant page,
 * the payload, the Total and the wallet guard all read from here, so they
 * cannot disagree on what the server will charge.
 */
export function pricePreOrderLine(
  product: FlexProduct,
  variants: FlexMenu["variants"],
  discounts: FlexMenu["discounts"],
  quantityByChoiceId: Record<string, number>
): PricedPreOrderLine {
  const variantDeposits = sumVariantDeposits(variants, quantityByChoiceId);

  return {
    unitPriceInclVat: discountedUnitPriceInclVat(
      product,
      discounts,
      extraUnitPriceInclVat(
        variants,
        quantityByChoiceId,
        product.taxRate.vatRate
      )
    ),
    depositInclVat:
      (product.deposit?.feeInclVat ?? 0) + variantDeposits.inclVat,
    variantDeposits
  };
}

/** What the wallet pays for the line: its price and its deposit. */
export const lineDueInclVat = (line: PreOrderLine) =>
  line.unitPriceInclVat + line.depositInclVat;
